import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { formatCurrency } from "../../utils/helpers";

/**
 * MonthlySpendingLineChart component - displays monthly spending trend as a line chart
 * @param {Object} props
 * @param {Array} props.data - Array of trend data with { date, total_mrc }
 */
const MonthlySpendingLineChart = ({ data = [] }) => {
  if (!data || data.length === 0) {
    return (
      <div className="rounded-xl bg-gray-50/80 border border-gray-100 p-8 text-center">
        <p className="text-sm text-gray-600">No trend data available</p>
      </div>
    );
  }

  // Format and sort data for chart chronologically
  // const chartData = data
  //   .map((item) => {
  //     const date = new Date(item.date);
  //     return {
  //       month: date.toLocaleDateString("en-US", { month: "short", year: "numeric" }),
  //       fullDate: date.toLocaleDateString("en-US", { month: "long", year: "numeric" }),
  //       spending: parseFloat(item.total_mrc || 0),
  //       date: item.date,
  //       timestamp: date.getTime(),
  //     };
  //   })
  //   .sort((a, b) => a.timestamp - b.timestamp);

  const chartData = data
  .map((item) => {
    // Expecting format: YYYY-MM-DD or YYYY-MM
    const [year, month] = item.date.split("-");

    const monthNames = [
      "Jan","Feb","Mar","Apr","May","Jun",
      "Jul","Aug","Sep","Oct","Nov","Dec"
    ];

    const monthIndex = parseInt(month, 10) - 1;

    return {
      month: `${monthNames[monthIndex]} ${year}`,
      fullDate: `${monthNames[monthIndex]} ${year}`,
      spending: parseFloat(item.total_mrc || 0),
      sortKey: `${year}-${month}` // safe sorting
    };
  })
  .sort((a, b) => a.sortKey.localeCompare(b.sortKey));

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white px-4 py-3 border border-gray-200 rounded-xl shadow-lg">
          <p className="text-sm font-semibold text-gray-900">{data.fullDate}</p>
          <p className="text-sm text-gray-600 mt-1.5">
            <span className="font-medium text-gray-700">Total spending</span>{" "}
            <span className="font-bold text-emerald-600 tabular-nums">{formatCurrency(data.spending)}</span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Format Y-axis values
  const formatYAxis = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value}`;
  };

  return (
    <div className="rounded-xl border border-gray-100 bg-white overflow-hidden">
      <div className="mb-4">
        <h3 className="text-base font-semibold text-gray-900">Monthly Spending Trend</h3>
        <p className="text-sm text-gray-500 mt-0.5">Track your spending over time</p>
      </div>
      <div style={{ width: "100%", height: "300px" }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={chartData}
            margin={{ top: 16, right: 24, left: 8, bottom: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
            <XAxis
              dataKey="month"
              stroke="#94a3b8"
              fontSize={12}
              tick={{ fill: "#64748b", fontWeight: 500 }}
              angle={-45}
              textAnchor="end"
              height={56}
              interval="preserveStartEnd"
              axisLine={{ stroke: "#e2e8f0" }}
              tickLine={{ stroke: "#e2e8f0" }}
            />
            <YAxis
              stroke="#94a3b8"
              fontSize={12}
              tick={{ fill: "#64748b", fontWeight: 500 }}
              tickFormatter={formatYAxis}
              width={56}
              axisLine={false}
              tickLine={{ stroke: "#e2e8f0" }}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#e2e8f0", strokeWidth: 1 }} />
            <Line
              type="monotone"
              dataKey="spending"
              stroke="#059669"
              strokeWidth={2.5}
              dot={{ fill: "#059669", r: 4, strokeWidth: 2, stroke: "#fff" }}
              activeDot={{ r: 6, fill: "#047857", stroke: "#fff", strokeWidth: 2 }}
              name="Spending"
              connectNulls={false}
              isAnimationActive={true}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      {/* <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-3.5 h-3.5 rounded-full bg-emerald-500 shadow-sm" />
          <span className="text-sm font-medium text-gray-700">Monthly Spending</span>
        </div>
        {chartData.length > 0 && (
          <div className="text-sm">
            <span className="text-gray-500">Latest: </span>
            <span className="font-semibold text-emerald-700">
              {formatCurrency(chartData[chartData.length - 1].spending)}
            </span>
          </div>
        )}
      </div> */}
    </div>
  );
};

export default MonthlySpendingLineChart;
